import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Moment from "react-moment";
import FilePreviewModal from "../../../layouts/FilePreview/FilePreviewModal";
import {
  formatCurrency,
  getTransactionFieldRows,
  isImagePath,
} from "../SCRV2DetailReport/SCRV2InvoiceDetailHelpers";
import { getReversalStatusMeta } from "./SCRV2ReversalStatusHelper";
import "./SCRV2TransactionReversalDetailModal.css";

const getPaymentTypeIcon = (paymentType) => {
  const value = (paymentType || "").toLowerCase();
  if (value.includes("cheque")) return "fa-money-check-alt";
  if (value.includes("cash")) return "fa-money-bill-wave";
  if (value.includes("bank") || value.includes("ibft")) return "fa-university";
  return "fa-wallet";
};

const getPaymentFieldIcon = (field) => {
  const label = (field.label || "").toLowerCase();
  const key = (field.fieldKey || "").toLowerCase();

  if (field.isFile || label.includes("image") || label.includes("upload") || label.includes("attachment")) {
    return field.isImage ? "fa-image" : "fa-paperclip";
  }
  if (field.isDate || label.includes("date")) return "fa-calendar-day";
  if (label.includes("bank") || key.includes("bank")) return "fa-university";
  if (label.includes("cheque") && label.includes("number")) return "fa-hashtag";
  if (label.includes("cheque")) return "fa-money-check-alt";
  if (label.includes("comment")) return "fa-comment-dots";
  if (label.includes("payer") || label.includes("name")) return "fa-user-tag";
  if (label.includes("amount")) return "fa-coins";
  return "fa-tag";
};

const DetailRow = ({ label, value, icon }) => (
  <div className="scr-reversal-detail__row">
    <div className="scr-reversal-detail__row-label">
      {icon ? (
        <span className="scr-reversal-detail__row-icon">
          <i className={`fas ${icon}`} aria-hidden="true" />
        </span>
      ) : null}
      <span>{label}</span>
    </div>
    <div className="scr-reversal-detail__row-value">{value}</div>
  </div>
);

const DetailCard = ({ title, children }) => (
  <div className="scr-reversal-detail__card">
    <div className="scr-reversal-detail__card-title">{title}</div>
    <div className="scr-reversal-detail__card-body">{children}</div>
  </div>
);

const mapDetailToTransaction = (detail) => ({
  id: detail.transactionId,
  transactionCategory: detail.transactionCategory,
  paymentTypeMethod:
    detail.paymentTypeName || detail.paymentType || detail.mfiType || detail.transactionCategory,
  amount: detail.amount,
  transactionDetail: detail.transactionDetails,
  comments: detail.comments,
  filePath: detail.filePath,
  dynamicData: detail.dynamicData,
});

const SCRV2TransactionReversalDetailModal = ({
  show,
  listItem,
  detail,
  loading,
  reopening,
  onClose,
  onReopen,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const transaction = detail ? mapDetailToTransaction(detail) : null;
  const paymentType =
    detail?.paymentTypeName ||
    detail?.paymentType ||
    transaction?.paymentTypeMethod ||
    listItem?.paymentType ||
    "-";
  const transactionCategory =
    detail?.transactionCategory || listItem?.transactionCategory || "";
  const fieldRows = transaction
    ? getTransactionFieldRows(transaction).filter((field) => {
      if (field.label === "Collected Amount" || field.label === "Payment Type") return false;
      if (field.label === "Transaction Category" && transactionCategory) return false;
      return true;
    })
    : [];
  const isReversed = detail?.isReversed === true || listItem?.isReversed === true;
  const reversalStatus = getReversalStatusMeta(listItem);
  const canReverse = reversalStatus.canReverse;
  const reversalDate = detail?.reversalDate;
  const scrNo = detail?.scrNo || listItem?.scrNo || "-";
  const distCenterName = detail?.distCenterName || listItem?.distCenterName || "-";
  const distributorName = detail?.distributorName || "-";
  const dlmName = detail?.dlmName || listItem?.dlmName || "-";
  const amount = formatCurrency(detail?.amount ?? listItem?.amount);
  const shopLabel = detail?.shopName
    ? `${detail.shopName}${detail?.vizShopCode ? ` (${detail.vizShopCode})` : ""}`
    : listItem?.shopCodeVizId || "-";
  const showPaymentDetailsLoading = loading && !detail;
  const invoiceNo = listItem?.vizproInvoiceNo || detail?.vizproInvoiceNumber || "-";

  useEffect(() => {
    if (!show) {
      setConfirmOpen(false);
      setFilePreview(null);
    }
  }, [show]);

  const openFilePreview = (field) => {
    const url = String(field.value || "").trim();
    if (!url) return;

    setFilePreview({
      url,
      title: field.label || "Attachment Preview",
      isImage: field.isImage ?? isImagePath(url),
    });
  };

  const renderFieldValue = (field) => {
    if (field.isDate && field.value) {
      return <Moment format="DD MMM YYYY">{field.value}</Moment>;
    }

    if (field.isFile && field.value) {
      return (
        <button
          type="button"
          className="scr-reversal-detail__file-link"
          onClick={() => openFilePreview(field)}
        >
          <i className="fas fa-paperclip" aria-hidden="true" />
          View attachment
        </button>
      );
    }

    return field.value ?? "-";
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        backdrop="static"
        className="scr-reversal-detail"
        dialogClassName="scr-reversal-detail__dialog"
      >
        <Modal.Header closeButton className="scr-reversal-detail__header border-0">
          <div className="scr-reversal-detail__header-inner">
            <span className="scr-reversal-detail__header-icon">
              <i className="fas fa-receipt" aria-hidden="true" />
            </span>
            <div className="scr-reversal-detail__header-text">
              <div className="scr-reversal-detail__header-top">
                <h5 className="scr-reversal-detail__title">Transaction Detail</h5>
                <span className={reversalStatus.pillClass}>{reversalStatus.label}</span>
              </div>
              <div className="scr-reversal-detail__refs">
                {invoiceNo !== "-" ? (
                  <span className="scr-reversal-detail__ref">
                    <i className="fas fa-file-invoice" aria-hidden="true" />
                    {invoiceNo}
                  </span>
                ) : null}
                {scrNo !== "-" ? (
                  <span className="scr-reversal-detail__ref">
                    <i className="fas fa-hashtag" aria-hidden="true" />
                    {scrNo}
                  </span>
                ) : null}
                {transactionCategory ? (
                  <span className="scr-reversal-detail__ref scr-reversal-detail__ref--muted">
                    {transactionCategory}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="scr-reversal-detail__body p-0">
          {listItem ? (
            <>
              <div className="scr-reversal-detail__hero">
                {isReversed && reversalDate ? (
                  <div className="scr-reversal-detail__hero-badge scr-reversal-detail__hero-badge--reversed">
                    <i className="fas fa-check-circle" aria-hidden="true" />
                    Reversed on <Moment format="DD MMM YYYY, HH:mm">{reversalDate}</Moment>
                  </div>
                ) : !canReverse && reversalStatus.reason ? (
                  <div className="scr-reversal-detail__hero-badge scr-reversal-detail__hero-badge--blocked">
                    <i className="fas fa-info-circle" aria-hidden="true" />
                    {reversalStatus.reason}
                  </div>
                ) : null}

                <div className="scr-reversal-detail__hero-amount">{amount}</div>
                <div className="scr-reversal-detail__hero-payment">
                  <i className={`fas ${getPaymentTypeIcon(paymentType)}`} aria-hidden="true" />
                  {paymentType}
                </div>
              </div>

              <div className="scr-reversal-detail__content">
                <DetailCard title="Transaction Info">
                  <DetailRow
                    icon="fa-calendar-alt"
                    label="Transaction date"
                    value={
                      detail?.transactionDate || listItem?.transactionDate ? (
                        <Moment format="DD MMM YYYY, HH:mm">
                          {detail?.transactionDate || listItem?.transactionDate}
                        </Moment>
                      ) : (
                        "-"
                      )
                    }
                  />
                  <DetailRow
                    icon="fa-building"
                    label="Distributor"
                    value={
                      distributorName !== "-"
                        ? distributorName
                        : showPaymentDetailsLoading
                          ? "Loading..."
                          : "-"
                    }
                  />
                  <DetailRow
                    icon="fa-map-marker-alt"
                    label="Distribution center"
                    value={distCenterName}
                  />
                  <DetailRow icon="fa-user" label="DLM" value={dlmName} />
                  <DetailRow icon="fa-store" label="Shop" value={shopLabel} />
                </DetailCard>

                {showPaymentDetailsLoading ? (
                  <div className="scr-reversal-detail__loading-row">
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                    Loading payment details...
                  </div>
                ) : null}

                {!showPaymentDetailsLoading && fieldRows.length > 0 ? (
                  <DetailCard title="Payment Details">
                    {fieldRows.map((field) => (
                      <DetailRow
                        key={field.label}
                        icon={getPaymentFieldIcon(field)}
                        label={field.label}
                        value={renderFieldValue(field)}
                      />
                    ))}
                  </DetailCard>
                ) : null}
              </div>
            </>
          ) : (
            <div className="scr-reversal-detail__empty">No transaction selected.</div>
          )}
        </Modal.Body>

        <Modal.Footer className="scr-reversal-detail__footer border-0">
          <Button
            variant="outline-secondary"
            className="scr-reversal-detail__close-btn"
            onClick={onClose}
            disabled={reopening}
          >
            Close
          </Button>
          {canReverse ? (
            <Button
              variant="danger"
              onClick={() => setConfirmOpen(true)}
              disabled={reopening || !listItem?.transactionId}
              className="scr-reversal-detail__reverse-btn"
            >
              <i className="fas fa-undo me-1" aria-hidden="true" />
              {reopening ? "Reversing..." : "Reverse transaction"}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>

      <Modal
        show={confirmOpen}
        onHide={() => setConfirmOpen(false)}
        centered
        backdrop="static"
        className="scr-finalize-modal"
      >
        <Modal.Body>
          <div className="scr-finalize-modal__icon">
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
          </div>
          <div className="scr-finalize-modal__title">Reverse Transaction?</div>
          <p className="scr-finalize-modal__message mb-0">
            This will reverse the transaction and reduce the invoice paid amount.
            You cannot undo this action.
          </p>
          {invoiceNo !== "-" || scrNo !== "-" ? (
            <span className="scr-finalize-modal__scr-no">
              {invoiceNo !== "-" ? `Invoice: ${invoiceNo}` : `SCR: ${scrNo}`}
              {amount ? ` · ${amount}` : ""}
            </span>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmOpen(false)}
            disabled={reopening}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="scr-finalize-modal__confirm-btn"
            onClick={async () => {
              setConfirmOpen(false);
              const success = await onReopen();
              if (success) {
                onClose();
              }
            }}
            disabled={reopening}
          >
            {reopening ? "Reversing..." : "Yes, Reverse"}
          </Button>
        </Modal.Footer>
      </Modal>

      <FilePreviewModal preview={filePreview} onClose={() => setFilePreview(null)} />
    </>
  );
};

export default SCRV2TransactionReversalDetailModal;
